import React, { useState } from "react";
import {
  Repeat,
  ScanFace,
  RotateCw,
  Zap,
  Radar,
  Move3d,
  UserCheck,
  KeyRound,
  Layers,
  ChevronRight,
  Code2,
} from "lucide-react";
import { useI18n } from "../../i18n";
import { Container, Section, SectionHead, Card, Badge } from "../../components/ui/Primitives";
import { PageHero, CTASection } from "../../components/marketing/Shared";
import { cn } from "../../lib/utils";

const CORE = [
  { icon: Repeat, title: "OAuth-style redirect flow", tech: "POST /liveness/start (Basic client_id:client_secret) → { session_token, verify_url, expires_at }; redirect to verify_url; callback to redirect_uri?code=&state=&status=; POST /liveness/result exchanges the one-time code.", desc: "Start a session, redirect the browser, collect the result — no SDK to embed." },
  { icon: ScanFace, title: "Passive liveness", tech: "Single-frame + short-clip liveness scoring while the user simply faces the camera; no explicit action required.", desc: "Frictionless anti-spoofing that runs in the background." },
  { icon: RotateCw, title: "Active gesture challenge", tech: "Randomized head-turn / blink / smile prompt bound to the session; proves an interactive, present human.", desc: "A live action the user performs on the spot." },
  { icon: Zap, title: "Flashmark active flash", tech: "Controlled screen-flash sequence; the reflected-light response distinguishes a real 3D face from a screen or print.", desc: "Screen-flash reflection confirms a genuine face." },
  { icon: Radar, title: "Multi-frame PAD", tech: "Presentation-attack detection over multiple frames catches printouts, replays, masks and rendered/deepfake faces.", desc: "Layered anti-spoofing across the whole capture." },
  { icon: Move3d, title: "Depth-from-motion", tech: "Structure-from-motion over the capture recovers 3D depth cues to reject flat and screen presentations.", desc: "3D depth cues from natural micro-movement." },
  { icon: UserCheck, title: "Document ↔ selfie match", tech: "ArcFace embeddings (insightface buffalo_sc, MobileFaceNet) score the live face against a reference photo or ID crop; frontal/quality gating for reliability.", desc: "Match the live face to a reference or ID." },
  { icon: KeyRound, title: "One-time code security", tech: "The browser only carries a short-lived, single-use code; the client backend exchanges it server-to-server over Basic auth, so the verdict can't be forged or replayed.", desc: "The biometric verdict never rides in the redirect." },
  { icon: Layers, title: "One verdict, layered defense", tech: "Multi-frame PAD, depth-from-motion and Flashmark combine with the synchronous gate into a single verdict; terminal verdicts are immutable and can never be flipped.", desc: "PAD, depth and Flashmark fold into one immutable verdict." },
];

const PIPELINE = [
  { key: "start", accent: "brand" },
  { key: "hosted", accent: "brand" },
  { key: "gate", accent: "exams" },
  { key: "code", accent: "exams" },
  { key: "fetch", accent: "exams" },
];

export default function Features() {
  const { t } = useI18n();
  const [tech, setTech] = useState(false);

  return (
    <>
      <PageHero
        breadcrumb={t("nav.features")}
        eyebrow={t("features.hero.eyebrow")}
        title={t("features.hero.title")}
        lead={t("features.hero.lead")}
      >
        <button
          onClick={() => setTech((v) => !v)}
          className={cn(
            "inline-flex items-center gap-2 rounded-xl border px-4 py-2.5 text-sm font-semibold transition-colors",
            tech ? "border-brand bg-brand/15 text-brand" : "border-white/25 text-white hover:bg-white/10"
          )}
        >
          <Code2 className="h-4 w-4" />
          {t("features.forEngineers")}
        </button>
      </PageHero>

      <Section>
        <Container>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {CORE.map((f) => (
              <Card key={f.title} className="p-6">
                <span className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-brand/12 text-brand">
                  <f.icon className="h-5 w-5" />
                </span>
                <h3 className="mt-4 text-base font-bold">{f.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{tech ? f.tech : f.desc}</p>
                {tech && (
                  <Badge tone="brand" className="mt-3">
                    <Code2 className="h-3 w-3" /> technical
                  </Badge>
                )}
              </Card>
            ))}
          </div>
        </Container>
      </Section>

      {/* Integration flow diagram */}
      <Section alt>
        <Container>
          <SectionHead center title={t("features.arch.title")} />
          <div className="mt-12 overflow-x-auto">
            <div className="flex min-w-max items-center justify-center gap-2 pb-2">
              {PIPELINE.map((p, i) => (
                <React.Fragment key={p.key}>
                  <div
                    className="flex h-24 w-44 flex-col items-center justify-center rounded-xl border bg-card p-3 text-center"
                    style={{ borderColor: `hsl(var(--${p.accent}) / 0.4)` }}
                  >
                    <span
                      className="mb-1 font-mono text-[10px] font-bold uppercase tracking-wider"
                      style={{ color: `hsl(var(--${p.accent}))` }}
                    >
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <span className="text-xs font-semibold leading-tight">{t(`features.arch.${p.key}`)}</span>
                  </div>
                  {i < PIPELINE.length - 1 && <ChevronRight className="h-5 w-5 shrink-0 text-muted-foreground" />}
                </React.Fragment>
              ))}
            </div>
          </div>
          <p className="mt-6 text-center font-mono text-xs text-muted-foreground">
            start → hosted page (face · flash · gesture) → liveness + deep PAD gate → one-time code → client fetch
          </p>
        </Container>
      </Section>

      <CTASection />
    </>
  );
}
